import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import { faLaptop, faGamepad, faRocket, faDollarSign, faCircleCheck } from '@fortawesome/free-solid-svg-icons';

import { currencyCode } from 'Helpers/Geo/data';
import Price from 'Components/Utility/Price/Price';

function BundleGamePoints({
  displayType,
  fullPrice,
  instantDelivery,
  price,
  showSteamCompatible,
  siteTheme,
  steamCompatible,
  totalGameCount,
  validDrm,
  deliveredGameCount,
  tiered,
  bundleSlug,
}) {
  let showPriceMessage = true;
  if (fullPrice[currencyCode] <= price[currencyCode]) showPriceMessage = false;

  let gamesCount = totalGameCount;
  // deliveredGameCount uses the Delivery Override in admin
  if (deliveredGameCount > totalGameCount) gamesCount = deliveredGameCount;

  return (
    <React.Fragment>
      <div className="BundlePoints BundlePoints--game">
        <div className="BundlePoints__points container">
          {showPriceMessage && !tiered &&
            <div className="BundlePoints__point">
              <div className="BundlePoints__dollarIcon BundlePoints__icon">
                <FontAwesomeIcon icon={faDollarSign} />
              </div>
              <div className="BundlePoints__pointText">
                <FormattedMessage
                  id="bundle-game-points.point.price"
                  defaultMessage="Get {fullPrice} worth of {displayType} for {price}"
                  values={{
                    fullPrice: <Price price={fullPrice} />,
                    price: <Price price={price} />,
                    displayType: displayType !== 'bundle' ? 'products' : 'games',
                  }}
                />
              </div>
            </div>
          }

          {showPriceMessage && tiered &&
            <div className="BundlePoints__point">
              <div className="BundlePoints__dollarIcon BundlePoints__icon">
                <FontAwesomeIcon icon={faDollarSign} />
              </div>
              <div className="BundlePoints__pointText">
                <FormattedMessage
                  id="bundle-game-points.point.price.tiered"
                  defaultMessage="Get up to {fullPrice} worth of {displayType}"
                  values={{
                    fullPrice: <Price price={fullPrice} />,
                    displayType: displayType !== 'bundle' ? 'products' : 'games',
                  }}
                />
              </div>
            </div>
          }

          {instantDelivery &&
            <div className="BundlePoints__point">
              <div className="BundlePoints__icon">
                <FontAwesomeIcon icon={faRocket} />
              </div>
              <div className="BundlePoints__pointText">
                <FormattedMessage
                  id="bundle-game-points.point.instant-delivery"
                  defaultMessage="Instant Delivery"
                />
              </div>
            </div>
          }

          <div className="BundlePoints__point">
            <div className="BundlePoints__icon">
              {displayType === 'bundle' &&
                <FontAwesomeIcon icon={faGamepad} />
              }
              {displayType !== 'bundle' &&
                <FontAwesomeIcon icon={faLaptop} />
              }
            </div>
            <div className="BundlePoints__pointText">
              {displayType === 'bundle' && bundleSlug !== 'runescape-siren-bundle-limited-edition' &&
                <FormattedMessage
                  id="bundle-game-points.point.game-count"
                  defaultMessage="{gamesCount, plural, one {# Awesome game} other {# Awesome games}}"
                  values={{ gamesCount }}
                />
              }
              {displayType === 'bundle' && bundleSlug === 'runescape-siren-bundle-limited-edition' &&
                <FormattedMessage
                  id="bundle-game-points.point.item-count"
                  defaultMessage="{gamesCount, plural, one {# Awesome item} other {# Awesome items}}"
                  values={{ gamesCount }}
                />
              }
              {displayType !== 'bundle' &&
                <FormattedMessage
                  id="bundle-game-points.point.non-game-count"
                  defaultMessage="{gamesCount, plural, one {# Awesome product} other {# Awesome products}}"
                  values={{ gamesCount }}
                />
              }
            </div>
          </div>

          <div className="BundlePoints__point">
            {validDrm[0] !== 'steam' &&
              <div className="BundlePoints__icon">
                <FontAwesomeIcon icon={faCircleCheck} />
              </div>
            }
            {validDrm[0] === 'steam' &&
              <div className="BundlePoints__steamIcon BundlePoints__icon">
                {siteTheme === 'dark' &&
                  <img src="https://cdn.fanatical.com/production/icons/drm/light/steam-icon.svg" alt="Steam" loading="lazy" />
                }
                {siteTheme === 'light' &&
                  <img src="https://cdn.fanatical.com/production/icons/drm/dark/steam-icon-grey-13.svg" alt="Steam" loading="lazy" />
                }
              </div>
            }
            <div className="BundlePoints__pointText">
              <FormattedMessage
                id="bundle-points.point.steam-redeem"
                defaultMessage="Redeem on {drm}"
                values={{ drm: validDrm[0] }}
              />
            </div>
          </div>

          {showSteamCompatible &&
            <div className="BundlePoints__point">
              <div className="BundlePoints__steamDeckIcon BundlePoints__icon">
                {siteTheme === 'dark' &&
                  <img src="https://cdn.fanatical.com/production/icons/steam-deck/light/steam-deck-icon.svg" alt="Steam Deck" loading="lazy" />
                }
                {siteTheme === 'light' &&
                  <img src="https://cdn.fanatical.com/production/icons/steam-deck/dark/steam-deck-icon.svg" alt="Steam Deck" loading="lazy" />
                }
              </div>
              <div className="BundlePoints__pointText">
                <FormattedMessage
                  id="bundle-game-points.point.steam-deck"
                  defaultMessage="{steamCompatible, plural, one {#  Steam Deck compatible game} other {# Steam Deck compatible games}}"
                  values={{
                    steamCompatible,
                  }}
                />
              </div>
            </div>
          }
        </div>
      </div>
    </React.Fragment>
  );
}

BundleGamePoints.propTypes = {
  displayType: PropTypes.string,
  fullPrice: PropTypes.shape({}),
  price: PropTypes.shape({}),
  showSteamCompatible: PropTypes.bool,
  siteTheme: PropTypes.string,
  steamCompatible: PropTypes.number,
  totalGameCount: PropTypes.number,
  validDrm: PropTypes.arrayOf(PropTypes.string),
  deliveredGameCount: PropTypes.number,
  tiered: PropTypes.bool,
  bundleSlug: PropTypes.string,
};

BundleGamePoints.defaultProps = {
  displayType: '',
  fullPrice: {},
  price: {},
  showSteamCompatible: false,
  siteTheme: 'dark',
  steamCompatible: null,
  totalGameCount: 0,
  validDrm: [],
  deliveredGameCount: 0,
  tiered: false,
  bundleSlug: '',
};

const mapStateToProps = ({
  siteTheme,
}) => ({
  siteTheme,
});

export default connect(mapStateToProps)(BundleGamePoints);
